import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createPrintletMapping,
  bulkCreatePrintletMappings,
  deletePrintletMapping,
  fetchMissingPrintletMappings,
  fetchPrintletCatalog,
  fetchPrintletGirabatTypes,
  fetchPrintletMappings,
  updatePrintletMapping,
} from "../../api/PrintletAPIs";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";
import {
  Link2,
  AlertTriangle,
  Plus,
  Pencil,
  Trash2,
  CheckCircle2,
  XCircle,
  Save,
  X,
  Inbox,
  Search,
  Layers,
  CheckSquare,
  Square,
  Loader,
  ChevronDown,
} from "lucide-react";

const emptyForm = {
  typeId: "",
  caseStyleId: "",
  printletProductId: "",
  printletProductRevision: 1,
  printletVariantId: "",
  printletVariantRevision: 1,
  printletPrintAreaId: "",
  printletPrintAreaRevision: 1,
  printletTechniqueId: "",
  printletTechniqueRevision: 1,
  placementPreset: "center",
  isActive: true,
};

function FormField({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1.5">{label}</label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 transition";

export default function PrintletMappingsPage() {
  const { authState } = useAuth();
  const canMutate = canMutateOrders(authState.role);
  const queryClient = useQueryClient();
  const { data: mappings = [] } = useQuery({ queryKey: ["printletMappings"], queryFn: fetchPrintletMappings });
  const { data: missing = [] } = useQuery({ queryKey: ["printletMissing"], queryFn: fetchMissingPrintletMappings });
  const { data: catalog } = useQuery({ queryKey: ["printletCatalog"], queryFn: fetchPrintletCatalog });
  const { data: types = [] } = useQuery({ queryKey: ["printletTypes"], queryFn: fetchPrintletGirabatTypes });
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mappingSearch, setMappingSearch] = useState("");
  const [bulkBrandId, setBulkBrandId] = useState("");
  const [bulkTypeSearch, setBulkTypeSearch] = useState("");
  const [selectedTypeIds, setSelectedTypeIds] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [missingOpen, setMissingOpen] = useState(false);

  const products = catalog?.products || [];
  const selectedProduct = products.find((p) => p.externalId === form.printletProductId);
  const selectedArea = selectedProduct?.printAreas?.find((a) => a.externalId === form.printletPrintAreaId);

  const techniques = useMemo(() => {
    if (!selectedArea?.techniques) return [];
    try {
      return typeof selectedArea.techniques === "string"
        ? JSON.parse(selectedArea.techniques)
        : selectedArea.techniques;
    } catch {
      return [];
    }
  }, [selectedArea]);

  const selectedTechnique = techniques.find((t) => t.id === form.printletTechniqueId);
  const presets =
    selectedTechnique?.placement_presets || selectedTechnique?.placementPresets || ["center"];

  const mappedTypeIds = useMemo(() => new Set(mappings.map((m) => Number(m.typeId))), [mappings]);

  const brands = useMemo(() => {
    const map = new Map();
    types.forEach((t) => {
      const id = String(t.brandId ?? "");
      if (!id) return;
      if (!map.has(id)) map.set(id, { brandId: id, name: t.brandName || `Brand #${id}` });
    });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "en"));
  }, [types]);

  const brandTypes = useMemo(() => {
    if (!bulkBrandId) return [];
    const term = bulkTypeSearch.trim().toLowerCase();
    return types
      .filter((t) => String(t.brandId) === String(bulkBrandId))
      .filter((t) => !term || String(t.name || "").toLowerCase().includes(term))
      .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "en"));
  }, [types, bulkBrandId, bulkTypeSearch]);

  const filteredMappings = useMemo(() => {
    const term = mappingSearch.trim().toLowerCase();
    if (!term) return mappings;
    return mappings.filter((m) =>
      [m.typeName, m.printletProductName, m.printletVariantSku, m.printletPrintAreaName, m.printletTechniqueName]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [mappings, mappingSearch]);

  const toggleType = (typeId) => {
    setSelectedTypeIds((prev) => {
      const next = new Set(prev);
      const id = Number(typeId);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setSelectAllBrandTypes = (checked) => {
    setSelectedTypeIds((prev) => {
      const next = new Set(prev);
      brandTypes.forEach((t) => {
        const id = Number(t.typeId);
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const runMapAllMissing = async () => {
    const count = missing.length;
    if (count === 0) {
      toast.info("كل الأنواع مربوطة");
      return;
    }
    if (!window.confirm(`ربط كل الأنواع غير المربوطة (${count}) على PHONE CASE؟\nالأنواع المربوطة مسبقاً هتتتخطى.`)) {
      return;
    }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const result = await bulkCreatePrintletMappings([], true);
      setBulkResult(result);
      toast.success(`نجاح ${result.success || 0} · فشل ${result.failed || 0} · متخطى ${result.skipped || 0}`);
      (result.results || [])
        .filter((r) => r.outcome === "failed")
        .slice(0, 8)
        .forEach((r) => toast.error(`#${r.typeName || r.typeId}: ${r.error}`));
      queryClient.invalidateQueries({ queryKey: ["printletMappings"] });
      queryClient.invalidateQueries({ queryKey: ["printletMissing"] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkRunning(false);
    }
  };

  const runBulkMapping = async () => {
    const ids = [...selectedTypeIds];
    if (ids.length === 0) {
      toast.info("حدد نوع واحد على الأقل");
      return;
    }
    const brandName = brands.find((b) => String(b.brandId) === String(bulkBrandId))?.name || "";
    if (!window.confirm(`ربط ${ids.length} نوع من ${brandName} على PHONE CASE؟\nالأنواع المربوطة مسبقاً هتتتخطى.`)) {
      return;
    }
    setBulkRunning(true);
    setBulkResult(null);
    try {
      const result = await bulkCreatePrintletMappings(ids);
      setBulkResult(result);
      toast.success(`نجاح ${result.success || 0} · فشل ${result.failed || 0} · متخطى ${result.skipped || 0}`);
      (result.results || [])
        .filter((r) => r.outcome === "failed")
        .slice(0, 8)
        .forEach((r) => toast.error(`#${r.typeName || r.typeId}: ${r.error}`));
      queryClient.invalidateQueries({ queryKey: ["printletMappings"] });
      queryClient.invalidateQueries({ queryKey: ["printletMissing"] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBulkRunning(false);
    }
  };

  const save = async () => {
    if (!form.typeId || !form.printletProductId || !form.printletVariantId) {
      toast.error("املأ الحقول الأساسية");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      typeId: Number(form.typeId),
      caseStyleId: form.caseStyleId ? Number(form.caseStyleId) : null,
      printletProductRevision: Number(form.printletProductRevision || 1),
      printletVariantRevision: Number(form.printletVariantRevision || 1),
      printletPrintAreaRevision: Number(form.printletPrintAreaRevision || 1),
      printletTechniqueRevision: Number(form.printletTechniqueRevision || 1),
    };
    try {
      if (editingId) await updatePrintletMapping(editingId, payload);
      else await createPrintletMapping(payload);
      toast.success("تم حفظ الربط");
      setForm(emptyForm);
      setEditingId(null);
      queryClient.invalidateQueries({ queryKey: ["printletMappings"] });
      queryClient.invalidateQueries({ queryKey: ["printletMissing"] });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      typeId: m.typeId,
      caseStyleId: m.caseStyleId || "",
      printletProductId: m.printletProductId,
      printletProductRevision: m.printletProductRevision,
      printletVariantId: m.printletVariantId,
      printletVariantRevision: m.printletVariantRevision,
      printletPrintAreaId: m.printletPrintAreaId,
      printletPrintAreaRevision: m.printletPrintAreaRevision,
      printletTechniqueId: m.printletTechniqueId,
      printletTechniqueRevision: m.printletTechniqueRevision,
      placementPreset: m.placementPreset,
      isActive: m.isActive,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const remove = async (id) => {
    if (!window.confirm("حذف الربط؟")) return;
    try {
      await deletePrintletMapping(id);
      toast.success("تم الحذف");
      queryClient.invalidateQueries({ queryKey: ["printletMappings"] });
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="space-y-5">
      <ToastContainer position="top-center" />

      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Link2 size={22} className="text-indigo-600" />
          ربط الأنواع
        </h2>
        <p className="text-sm text-slate-500 mt-1">ربط أنواع جراباتكم بمنتجات Printlet</p>
      </div>

      {canMutate && (
        <div className="bg-white rounded-2xl border border-indigo-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
              <Layers size={16} />
            </div>
            <div>
              <h3 className="font-bold text-slate-800">ربط جماعي</h3>
              <p className="text-xs text-slate-500">PHONE CASE في Printlet له variant واحد — الموديل بيتبعت مع الطلب</p>
            </div>
          </div>

          <button
              type="button"
              onClick={runMapAllMissing}
              disabled={bulkRunning || missing.length === 0}
              className="mb-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-60"
            >
              {bulkRunning ? <Loader size={16} className="animate-spin" /> : <Layers size={16} />}
              ربط كل الأنواع غير المربوطة ({missing.length})
            </button>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <FormField label="البراند">
              <select
                className={inputCls}
                value={bulkBrandId}
                onChange={(e) => {
                  setBulkBrandId(e.target.value);
                  setSelectedTypeIds(new Set());
                  setBulkTypeSearch("");
                  setBulkResult(null);
                }}
              >
                <option value="">اختر البراند</option>
                {brands.map((b) => (
                  <option key={b.brandId} value={b.brandId}>{b.name}</option>
                ))}
              </select>
            </FormField>
            <FormField label="بحث داخل أنواع البراند">
              <div className="relative">
                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  className={`${inputCls} pr-9`}
                  value={bulkTypeSearch}
                  onChange={(e) => setBulkTypeSearch(e.target.value)}
                  placeholder="مثلاً iPhone 15"
                  disabled={!bulkBrandId}
                />
              </div>
            </FormField>
          </div>

          {bulkBrandId && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-sm text-slate-600">
                  ظاهر: {brandTypes.length} · محدد: {selectedTypeIds.size}
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectAllBrandTypes(true)}
                    disabled={brandTypes.length === 0 || bulkRunning}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-indigo-200 text-indigo-700 text-xs font-medium hover:bg-indigo-50 disabled:opacity-50"
                  >
                    <CheckSquare size={14} /> تحديد الكل
                  </button>
                  <button
                    type="button"
                    onClick={() => setSelectAllBrandTypes(false)}
                    disabled={selectedTypeIds.size === 0 || bulkRunning}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 text-xs font-medium hover:bg-slate-50 disabled:opacity-50"
                  >
                    <Square size={14} /> إلغاء التحديد
                  </button>
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl divide-y divide-slate-100 mb-4">
                {brandTypes.length === 0 ? (
                  <p className="p-4 text-sm text-slate-500">لا توجد أنواع لهذا البراند</p>
                ) : (
                  brandTypes.map((t) => {
                    const id = Number(t.typeId);
                    const already = mappedTypeIds.has(id);
                    return (
                      <label key={id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-indigo-600"
                          checked={selectedTypeIds.has(id)}
                          onChange={() => toggleType(id)}
                          disabled={bulkRunning}
                        />
                        <span className="flex-1 text-sm text-slate-800">{t.name}</span>
                        {already && (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                            مربوط
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>

              <button
                type="button"
                onClick={runBulkMapping}
                disabled={bulkRunning || selectedTypeIds.size === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
              >
                {bulkRunning ? <Loader size={16} className="animate-spin" /> : <Layers size={16} />}
                بدء الربط الجماعي ({selectedTypeIds.size})
              </button>
            </>
          )}

          {bulkResult && (
            <div className="mt-4 border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-slate-50 text-sm text-slate-700">
                نتيجة {bulkResult.productName || "PHONE CASE"}: نجاح {bulkResult.success || 0} · فشل {bulkResult.failed || 0} · متخطى {bulkResult.skipped || 0}
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-slate-100">
                {(bulkResult.results || []).map((r) => (
                  <div key={r.typeId} className="px-3 py-2 text-sm flex items-start justify-between gap-3">
                    <span className="font-medium text-slate-800">{r.typeName || `#${r.typeId}`}</span>
                    <span className="text-left text-xs">
                      {r.outcome === "success" && (
                        <span className="text-emerald-700">تم الربط{r.variantSku ? ` — ${r.variantSku}` : ""}</span>
                      )}
                      {r.outcome === "skipped" && <span className="text-amber-700">{r.error || "تم تخطيه"}</span>}
                      {r.outcome === "failed" && <span className="text-rose-700">{r.error || "فشل"}</span>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {missing.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl overflow-hidden">
          <button
            type="button"
            onClick={() => setMissingOpen((open) => !open)}
            className="w-full flex items-center gap-3 p-4 text-right"
            aria-expanded={missingOpen}
          >
            <div className="p-2 rounded-xl bg-amber-100 text-amber-700 shrink-0">
              <AlertTriangle size={18} />
            </div>
            <b className="flex-1 text-sm text-amber-900">
              أنواع بدون ربط Printlet ({missing.length})
            </b>
            <ChevronDown
              size={18}
              className={`text-amber-700 shrink-0 transition-transform ${missingOpen ? "rotate-180" : ""}`}
            />
          </button>
          {missingOpen && (
            <div className="px-4 pb-4 flex flex-wrap gap-1.5">
              {missing.map((t) => (
                <span key={t.typeId || t.name} className="px-2 py-0.5 bg-white rounded-lg border border-amber-200 text-xs">
                  {t.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Form */}
      {canMutate && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100">
              {editingId ? <Pencil size={16} /> : <Plus size={16} />}
            </div>
            <h3 className="font-bold text-slate-800">
              {editingId ? "تعديل الربط" : "إضافة ربط جديد"}
            </h3>
            {editingId && (
              <button
                onClick={cancelEdit}
                className="mr-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-rose-600 transition"
              >
                <X size={13} /> إلغاء التعديل
              </button>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <FormField label="نوع جراباتكم *">
              <select className={inputCls} value={form.typeId} onChange={(e) => setForm({ ...form, typeId: e.target.value })}>
                <option value="">اختر النوع</option>
                {types.map((t) => (
                  <option key={t.typeId} value={t.typeId}>{t.name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="منتج Printlet *">
              <select
                className={inputCls}
                value={form.printletProductId}
                onChange={(e) => {
                  const product = products.find((p) => p.externalId === e.target.value);
                  setForm({
                    ...form,
                    printletProductId: e.target.value,
                    printletProductRevision: product?.revision || 1,
                    printletVariantId: "",
                    printletPrintAreaId: "",
                    printletTechniqueId: "",
                  });
                }}
              >
                <option value="">اختر المنتج</option>
                {products.map((p) => (
                  <option key={p.externalId} value={p.externalId}>{p.name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Variant *">
              <select
                className={inputCls}
                value={form.printletVariantId}
                onChange={(e) => {
                  const variant = selectedProduct?.variants?.find((v) => v.externalId === e.target.value);
                  setForm({ ...form, printletVariantId: e.target.value, printletVariantRevision: variant?.revision || 1 });
                }}
              >
                <option value="">اختر Variant</option>
                {(selectedProduct?.variants || []).map((v) => (
                  <option key={v.externalId} value={v.externalId}>{v.sku || v.externalId}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Print Area">
              <select
                className={inputCls}
                value={form.printletPrintAreaId}
                onChange={(e) => {
                  const area = selectedProduct?.printAreas?.find((a) => a.externalId === e.target.value);
                  setForm({
                    ...form,
                    printletPrintAreaId: e.target.value,
                    printletPrintAreaRevision: area?.revision || 1,
                    printletTechniqueId: "",
                  });
                }}
              >
                <option value="">اختر المساحة</option>
                {(selectedProduct?.printAreas || []).map((a) => (
                  <option key={a.externalId} value={a.externalId}>{a.name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Technique">
              <select
                className={inputCls}
                value={form.printletTechniqueId}
                onChange={(e) => {
                  const tech = techniques.find((t) => t.id === e.target.value);
                  setForm({
                    ...form,
                    printletTechniqueId: e.target.value,
                    printletTechniqueRevision: tech?.revision || 1,
                    placementPreset:
                      (tech?.placement_presets || tech?.placementPresets || ["center"])[0] || "center",
                  });
                }}
              >
                <option value="">اختر التقنية</option>
                {techniques.map((t) => (
                  <option key={t.id} value={t.id}>{t.name || t.technique_name}</option>
                ))}
              </select>
            </FormField>

            <FormField label="Placement Preset">
              <select
                className={inputCls}
                value={form.placementPreset}
                onChange={(e) => setForm({ ...form, placementPreset: e.target.value })}
              >
                {presets.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </FormField>
          </div>

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-100">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-700">نشط</span>
            </label>

            <button
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition"
            >
              <Save size={16} />
              {editingId ? "تحديث الربط" : "حفظ الربط"}
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex flex-col md:flex-row md:items-center gap-3">
          <h3 className="font-bold text-slate-800 shrink-0">الأنواع المربوطة</h3>
          <div className="relative flex-1">
            <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              className={`${inputCls} pr-9`}
              value={mappingSearch}
              onChange={(e) => setMappingSearch(e.target.value)}
              placeholder="بحث باسم النوع أو الـ variant..."
            />
          </div>
          <span className="text-xs text-slate-500">{filteredMappings.length} / {mappings.length}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-slate-500 text-xs uppercase">
                <th className="p-3 text-right font-semibold">جراباتكم</th>
                <th className="p-3 text-right font-semibold">Printlet</th>
                <th className="p-3 text-right font-semibold">الإعداد</th>
                <th className="p-3 text-right font-semibold">الحالة</th>
                {canMutate && <th className="p-3 text-right font-semibold"></th>}
              </tr>
            </thead>
            <tbody>
              {filteredMappings.length === 0 ? (
                <tr>
                  <td colSpan={canMutate ? 5 : 4}>
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                      <div className="p-4 rounded-full bg-slate-100 mb-3">
                        <Inbox size={28} className="text-slate-400" />
                      </div>
                      <p className="text-sm text-slate-500">
                        {mappings.length === 0 ? "لا توجد روابط بعد" : "لا توجد نتائج للبحث"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredMappings.map((m) => (
                  <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition">
                    <td className="p-3">
                      <span className="font-medium text-slate-800">{m.typeName}</span>
                    </td>
                    <td className="p-3">
                      <div className="text-slate-800">{m.printletProductName}</div>
                      <div className="text-xs text-slate-500 font-mono">{m.printletVariantSku}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                          {m.printletPrintAreaName}
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                          {m.printletTechniqueName}
                        </span>
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-xs">
                          {m.placementPreset}
                        </span>
                      </div>
                    </td>
                    <td className="p-3">
                      {m.isValid ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
                          <CheckCircle2 size={11} /> صالح
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-xs font-medium border border-rose-200"
                          title={m.invalidReason}
                        >
                          <XCircle size={11} /> {m.invalidReason || "غير صالح"}
                        </span>
                      )}
                      {!m.isActive && (
                        <span className="mr-1 inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs border border-slate-200">
                          معطّل
                        </span>
                      )}
                    </td>
                    {canMutate && (
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => startEdit(m)}
                            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition"
                            title="تعديل"
                          >
                            <Pencil size={15} />
                          </button>
                          <button
                            onClick={() => remove(m.id)}
                            className="p-2 rounded-lg text-rose-600 hover:bg-rose-50 transition"
                            title="حذف"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}